import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { jwtDecode } from "jwt-decode";
import { finalize, firstValueFrom, from, lastValueFrom, Observable } from "rxjs";
import { ENVIRONMENT } from "../../environments/environment";
import { ApplicationService } from "../services/application.service";

export interface Match {
  match: string;
  url: string;
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private msalService: MsalService;
  private application: ApplicationService;

  public constructor() {
    this.msalService = inject(MsalService);
    this.application = inject(ApplicationService);
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.headers.get("skip-global-loader") != "true") this.application.busy.next(true);
    const options = ENVIRONMENT.INTERCEPT;
    const found = options.find((match: Match) => (match.match == "full" ? req.url.startsWith(match.url) : req.url.includes(match.url)));

    if (found) {
      console.log("Found", found);

      switch (found.trigger) {
        case "default":
          return from(this.handle(req, next));

        case "smartlink":
          return from(this.handleSmartlink(req, next));

        case "graph":
          return from(this.handleGraph(req, next));

        case "none":
          return next.handle(req).pipe(finalize(() => this.onRequestEnd(req)));

        default:
          return from(this.handle(req, next));
      }
    } else {
      return next.handle(req).pipe(finalize(() => this.onRequestEnd(req)));
    }
  }

  private async handle(req: HttpRequest<unknown>, next: HttpHandler): Promise<HttpEvent<unknown>> {
    const token = await firstValueFrom(this.msalService.acquireTokenSilent({ scopes: ENVIRONMENT.MSAL.scopes }));
    sessionStorage.setItem("accessToken", token.accessToken);

    const request = req.clone({
      setHeaders: {
        Authorization: req.headers.get("Authorization") || `Bearer ${sessionStorage.getItem("accessToken")}`,
        // "smartlink-bo-token": this.getSmartlinkToken() ?? "null",
      },
    });
    return lastValueFrom(next.handle(request).pipe(finalize(() => this.onRequestEnd(req))));
  }

  private async handleGraph(req: HttpRequest<unknown>, next: HttpHandler): Promise<HttpEvent<unknown>> {
    const token = await firstValueFrom(this.msalService.acquireTokenSilent({ scopes: ENVIRONMENT.MSAL.scopes }));
    sessionStorage.setItem("accessToken", token.accessToken);

    const request = req.clone({
      setHeaders: {
        Authorization: req.headers.get("Authorization") || `Bearer ${sessionStorage.getItem("accessToken")}`,
      },
    });
    return lastValueFrom(next.handle(request).pipe(finalize(() => this.onRequestEnd(req))));
  }

  private async handleSmartlink(req: HttpRequest<unknown>, next: HttpHandler): Promise<HttpEvent<unknown>> {
    const request = req.clone({
      setHeaders: {
        Authorization: `Bearer ${this.getSmartlinkToken()}`,
      },
    });
    return lastValueFrom(next.handle(request).pipe(finalize(() => this.onRequestEnd(req))));
  }

  private getSmartlinkToken(): string | null {
    const smartlinkToken = sessionStorage.getItem("smartlink-bo-token");
    try {
      if (smartlinkToken) {
        const expiry = jwtDecode<{ exp: number }>(smartlinkToken).exp;
        if (expiry < new Date().getTime() / 1000) throw new Error("smartlink-bo-token is expired.");
      } else {
        throw new Error("Missing smartlink-bo-token.");
      }
    } catch (err) {
      sessionStorage.clear();
      location.reload();
    }

    return smartlinkToken;
  }

  private onRequestEnd(req: HttpRequest<unknown>): void {
    if (req.headers.get("skip-global-loader") != "true") this.application.busy.next(false);
  }
}
